import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Target, Trophy, GraduationCap, Star, Heart, Flame, 
  Plus, Edit2, Trash2, Check, Clock, Calendar,
  TrendingUp, Award, Users, MapPin, Link as LinkIcon,
  Instagram, Twitter, Youtube, Share2, Download,
  Zap, Timer, Ruler, Weight, ChevronRight, Sparkles,
  Quote, Eye, School, CheckCircle, Circle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

// Popular schools database
const POPULAR_SCHOOLS = [
  { id: 'alabama', name: 'University of Alabama', conference: 'SEC', mascot: 'Crimson Tide' },
  { id: 'ohio_state', name: 'Ohio State University', conference: 'Big Ten', mascot: 'Buckeyes' },
  { id: 'georgia', name: 'University of Georgia', conference: 'SEC', mascot: 'Bulldogs' },
  { id: 'michigan', name: 'University of Michigan', conference: 'Big Ten', mascot: 'Wolverines' },
  { id: 'clemson', name: 'Clemson University', conference: 'ACC', mascot: 'Tigers' },
  { id: 'texas', name: 'University of Texas', conference: 'SEC', mascot: 'Longhorns' },
  { id: 'usc', name: 'USC', conference: 'Big Ten', mascot: 'Trojans' },
  { id: 'notre_dame', name: 'Notre Dame', conference: 'Independent', mascot: 'Fighting Irish' },
  { id: 'lsu', name: 'Louisiana State University', conference: 'SEC', mascot: 'Tigers' },
  { id: 'oklahoma', name: 'University of Oklahoma', conference: 'SEC', mascot: 'Sooners' },
  { id: 'penn_state', name: 'Penn State University', conference: 'Big Ten', mascot: 'Nittany Lions' },
  { id: 'florida', name: 'University of Florida', conference: 'SEC', mascot: 'Gators' },
  { id: 'oregon', name: 'University of Oregon', conference: 'Big Ten', mascot: 'Ducks' },
  { id: 'tennessee', name: 'University of Tennessee', conference: 'SEC', mascot: 'Volunteers' },
  { id: 'miami', name: 'University of Miami', conference: 'ACC', mascot: 'Hurricanes' },
  { id: 'ucla', name: 'UCLA', conference: 'Big Ten', mascot: 'Bruins' },
  { id: 'auburn', name: 'Auburn University', conference: 'SEC', mascot: 'Tigers' },
  { id: 'texas_am', name: 'Texas A&M University', conference: 'SEC', mascot: 'Aggies' },
  { id: 'wisconsin', name: 'University of Wisconsin', conference: 'Big Ten', mascot: 'Badgers' },
  { id: 'duke', name: 'Duke University', conference: 'ACC', mascot: 'Blue Devils' },
  { id: 'stanford', name: 'Stanford University', conference: 'ACC', mascot: 'Cardinal' },
  { id: 'other', name: 'Other School', conference: '', mascot: '' },
];

// Combine metrics by sport
const COMBINE_METRICS = {
  football: [
    { id: '40_yard', name: '40-Yard Dash', unit: 'sec', icon: Timer, goodValue: 4.5, eliteValue: 4.3 },
    { id: 'vertical', name: 'Vertical Jump', unit: 'in', icon: TrendingUp, goodValue: 32, eliteValue: 38 },
    { id: 'broad_jump', name: 'Broad Jump', unit: 'in', icon: Ruler, goodValue: 108, eliteValue: 120 },
    { id: 'bench', name: 'Bench Press (225)', unit: 'reps', icon: Weight, goodValue: 15, eliteValue: 25 },
    { id: 'shuttle', name: '20-Yard Shuttle', unit: 'sec', icon: Zap, goodValue: 4.3, eliteValue: 4.0 },
    { id: 'three_cone', name: '3-Cone Drill', unit: 'sec', icon: Target, goodValue: 7.2, eliteValue: 6.8 },
  ],
  basketball: [
    { id: 'vertical', name: 'Vertical Jump', unit: 'in', icon: TrendingUp, goodValue: 28, eliteValue: 36 },
    { id: 'lane_agility', name: 'Lane Agility', unit: 'sec', icon: Zap, goodValue: 11.5, eliteValue: 10.5 },
    { id: 'sprint', name: '3/4 Court Sprint', unit: 'sec', icon: Timer, goodValue: 3.3, eliteValue: 3.0 },
    { id: 'shuttle', name: 'Shuttle Run', unit: 'sec', icon: Target, goodValue: 3.0, eliteValue: 2.8 },
  ],
  soccer: [
    { id: '40_yard', name: '40-Yard Dash', unit: 'sec', icon: Timer, goodValue: 4.8, eliteValue: 4.5 },
    { id: 'beep_test', name: 'Beep Test', unit: 'level', icon: TrendingUp, goodValue: 10, eliteValue: 13 },
    { id: 'vertical', name: 'Vertical Jump', unit: 'in', icon: TrendingUp, goodValue: 24, eliteValue: 30 },
  ],
  default: [
    { id: '40_yard', name: '40-Yard Dash', unit: 'sec', icon: Timer, goodValue: 5.0, eliteValue: 4.5 },
    { id: 'vertical', name: 'Vertical Jump', unit: 'in', icon: TrendingUp, goodValue: 26, eliteValue: 32 },
    { id: 'mile', name: 'Mile Run', unit: 'min', icon: Clock, goodValue: 7, eliteValue: 5.5 },
  ]
};

const AthleteProfilePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('goals');
  
  // Profile data
  const [profile, setProfile] = useState({
    // Commitment & Why
    whyStatement: '',
    commitmentLevel: 'all-in', // all-in, dedicated, casual
    
    // Dream Schools
    dreamSchools: [],
    
    // Goals
    shortTermGoals: [],
    longTermGoals: [],
    careerGoal: '',
    
    // Combine Metrics
    metrics: {},
    
    // Social & Links
    highlightReelUrl: '',
    hudlUrl: '',
    instagramHandle: '',
    twitterHandle: '',
    
    // Stats
    height: '',
    weight: '',
    gpa: '',
    satScore: '',
    actScore: '',
    
    // Recruitment
    recruitmentStatus: 'uncommitted', // uncommitted, verbal, signed
    interestedSchools: [],
    offers: [],
  });

  // Dialog states
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [showSchoolDialog, setShowSchoolDialog] = useState(false);
  const [showMetricDialog, setShowMetricDialog] = useState(false);
  const [showWhyDialog, setShowWhyDialog] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [goalType, setGoalType] = useState('short'); // short or long

  // New goal form
  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    deadline: '',
    category: 'training', // training, academics, nutrition, mental
    completed: false
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/athlete/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        // Ensure arrays are properly initialized
        const profileData = {
          ...response.data,
          shortTermGoals: response.data.shortTermGoals || [],
          longTermGoals: response.data.longTermGoals || [],
          dreamSchools: response.data.dreamSchools || [],
          interestedSchools: response.data.interestedSchools || [],
          offers: response.data.offers || [],
          metrics: response.data.metrics || {}
        };
        setProfile(prev => ({ ...prev, ...profileData }));
      }
    } catch (error) {
      // Profile doesn't exist yet, use defaults
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async (updates) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('aura_token');
      // Merge updates with current profile, ensuring arrays are preserved
      const updatedProfile = {
        ...profile,
        ...updates,
        // Ensure arrays are never null
        shortTermGoals: updates.shortTermGoals ?? profile.shortTermGoals ?? [],
        longTermGoals: updates.longTermGoals ?? profile.longTermGoals ?? [],
        dreamSchools: updates.dreamSchools ?? profile.dreamSchools ?? [],
        interestedSchools: updates.interestedSchools ?? profile.interestedSchools ?? [],
        offers: updates.offers ?? profile.offers ?? [],
        metrics: updates.metrics ?? profile.metrics ?? {}
      };
      
      await axios.put(`${API}/athlete/profile`, 
        updatedProfile,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfile(updatedProfile);
      toast.success('Profile updated!');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addGoal = () => {
    if (!newGoal.title) return;
    
    const goal = {
      id: Date.now().toString(),
      ...newGoal,
      createdAt: new Date().toISOString()
    };

    const key = goalType === 'short' ? 'shortTermGoals' : 'longTermGoals';
    const updatedGoals = [...profile[key], goal];
    
    saveProfile({ [key]: updatedGoals });
    setNewGoal({ title: '', description: '', deadline: '', category: 'training', completed: false });
    setShowGoalDialog(false);
  };

  const toggleGoalComplete = (goalId, type) => {
    const key = type === 'short' ? 'shortTermGoals' : 'longTermGoals';
    const updatedGoals = profile[key].map(g => 
      g.id === goalId ? { ...g, completed: !g.completed } : g
    );
    saveProfile({ [key]: updatedGoals });
  };

  const deleteGoal = (goalId, type) => {
    const key = type === 'short' ? 'shortTermGoals' : 'longTermGoals';
    const updatedGoals = profile[key].filter(g => g.id !== goalId);
    saveProfile({ [key]: updatedGoals });
  };

  const addDreamSchool = (schoolId) => {
    if (profile.dreamSchools.length >= 5) {
      toast.error('Maximum 5 dream schools');
      return;
    }
    if (profile.dreamSchools.includes(schoolId)) {
      toast.error('School already added');
      return;
    }
    const updatedSchools = [...profile.dreamSchools, schoolId];
    saveProfile({ dreamSchools: updatedSchools });
    setShowSchoolDialog(false);
  };

  const removeDreamSchool = (schoolId) => {
    const updatedSchools = profile.dreamSchools.filter(s => s !== schoolId);
    saveProfile({ dreamSchools: updatedSchools });
  };

  const updateMetric = (metricId, value) => {
    const updatedMetrics = { ...profile.metrics, [metricId]: value };
    saveProfile({ metrics: updatedMetrics });
  };

  const getSchoolById = (id) => POPULAR_SCHOOLS.find(s => s.id === id);
  
  const sportMetrics = COMBINE_METRICS[user?.sport?.toLowerCase()] || COMBINE_METRICS.default;

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'training': return Zap;
      case 'academics': return GraduationCap;
      case 'nutrition': return Heart;
      case 'mental': return Sparkles;
      default: return Target;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'training': return 'bg-aura-primary text-white';
      case 'academics': return 'bg-blue-500 text-white';
      case 'nutrition': return 'bg-green-500 text-white';
      case 'mental': return 'bg-purple-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const completedShortTerm = profile.shortTermGoals.filter(g => g.completed).length;
  const completedLongTerm = profile.longTermGoals.filter(g => g.completed).length;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white uppercase tracking-tight">
              My Profile
            </h1>
            <p className="text-aura-text-muted mt-1">Your goals, dreams, and why you grind</p>
          </div>
          <Button 
            onClick={() => navigate('/share')}
            className="bg-aura-secondary hover:bg-aura-secondary/90 text-white"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share Profile
          </Button>
        </div>

        {/* Commitment Statement - Always Visible */}
        <Card className="bg-gradient-to-r from-aura-primary/20 to-aura-secondary/20 border-aura-primary/30">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-aura-primary rounded-xl flex items-center justify-center flex-shrink-0">
                <Quote className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-bold text-lg text-white uppercase tracking-wider mb-2">
                  My "Why"
                </h3>
                {profile.whyStatement ? (
                  <div>
                    <p className="text-white italic text-lg">"{profile.whyStatement}"</p>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => setShowWhyDialog(true)}
                      className="text-aura-text-muted mt-2"
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  </div>
                ) : (
                  <div>
                    <p className="text-aura-text-muted mb-3">
                      Why do you train? What drives you to be great? Write your commitment statement to fuel your consistency.
                    </p>
                    <Button 
                      onClick={() => setShowWhyDialog(true)}
                      className="bg-aura-primary hover:bg-aura-primary-hover text-white"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Write Your "Why"
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dream Schools */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                <School className="w-5 h-5 text-aura-secondary" />
                Dream Schools ({profile.dreamSchools.length}/5)
              </CardTitle>
              {profile.dreamSchools.length < 5 && (
                <Button 
                  size="sm" 
                  onClick={() => setShowSchoolDialog(true)}
                  className="bg-aura-secondary hover:bg-aura-secondary/90 text-white"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add School
                </Button>
              )}
            </div>
            <p className="text-sm text-aura-text-muted mt-1">
              Your top 5 target schools - visualize where you want to play
            </p>
          </CardHeader>
          <CardContent>
            {profile.dreamSchools.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {profile.dreamSchools.map((schoolId, idx) => {
                  const school = getSchoolById(schoolId);
                  if (!school) return null;
                  return (
                    <div 
                      key={schoolId}
                      className="relative group p-4 bg-gradient-to-br from-white/10 to-white/5 rounded-xl border border-white/10 hover:border-aura-secondary/50 transition-all"
                    >
                      <button
                        onClick={() => removeDreamSchool(schoolId)}
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-red-500/20 rounded-full"
                      >
                        <Trash2 className="w-3 h-3 text-red-400" />
                      </button>
                      <div className="text-center">
                        <div className="w-12 h-12 mx-auto mb-2 bg-aura-secondary/20 rounded-full flex items-center justify-center">
                          <span className="font-heading font-bold text-xl text-aura-secondary">
                            #{idx + 1}
                          </span>
                        </div>
                        <p className="font-medium text-white text-sm">{school.name}</p>
                        <p className="text-xs text-aura-text-muted">{school.conference}</p>
                      </div>
                    </div>
                  );
                })}
                {/* Empty slots */}
                {Array.from({ length: 5 - profile.dreamSchools.length }).map((_, idx) => (
                  <button
                    key={`empty-${idx}`}
                    onClick={() => setShowSchoolDialog(true)}
                    className="p-4 border-2 border-dashed border-white/10 rounded-xl hover:border-aura-secondary/30 transition-colors"
                  >
                    <div className="text-center">
                      <div className="w-12 h-12 mx-auto mb-2 bg-white/5 rounded-full flex items-center justify-center">
                        <Plus className="w-6 h-6 text-aura-text-muted" />
                      </div>
                      <p className="text-sm text-aura-text-muted">Add School</p>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <GraduationCap className="w-12 h-12 text-aura-text-subtle mx-auto mb-3" />
                <p className="text-aura-text-muted mb-4">
                  Add your top 5 dream schools to stay focused on your goals
                </p>
                <Button 
                  onClick={() => setShowSchoolDialog(true)}
                  className="bg-aura-secondary hover:bg-aura-secondary/90 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Dream School
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs for Goals, Metrics, Profile */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white/5 border border-white/10 p-1">
            <TabsTrigger value="goals" className="data-[state=active]:bg-aura-primary data-[state=active]:text-white">
              <Target className="w-4 h-4 mr-2" />
              Goals
            </TabsTrigger>
            <TabsTrigger value="metrics" className="data-[state=active]:bg-aura-primary data-[state=active]:text-white">
              <TrendingUp className="w-4 h-4 mr-2" />
              Metrics
            </TabsTrigger>
            <TabsTrigger value="profile" className="data-[state=active]:bg-aura-primary data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
          </TabsList>

          {/* Goals Tab */}
          <TabsContent value="goals" className="mt-6 space-y-6">
            {/* Short Term Goals */}
            <Card className="bg-aura-surface border-white/10">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                      <Clock className="w-5 h-5 text-yellow-400" />
                      Short-Term Goals
                    </CardTitle>
                    <p className="text-sm text-aura-text-muted mt-1">
                      This week/month focus ({completedShortTerm}/{profile.shortTermGoals.length} complete)
                    </p>
                  </div>
                  <Button 
                    size="sm"
                    onClick={() => { setGoalType('short'); setShowGoalDialog(true); }}
                    className="bg-yellow-500 hover:bg-yellow-600 text-white"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Goal
                  </Button>
                </div>
                {profile.shortTermGoals.length > 0 && (
                  <Progress 
                    value={(completedShortTerm / profile.shortTermGoals.length) * 100} 
                    className="h-2 mt-3" 
                  />
                )}
              </CardHeader>
              <CardContent>
                {profile.shortTermGoals.length > 0 ? (
                  <div className="space-y-3">
                    {profile.shortTermGoals.map((goal) => {
                      const Icon = getCategoryIcon(goal.category);
                      return (
                        <div 
                          key={goal.id}
                          className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                            goal.completed ? 'bg-green-500/10 border border-green-500/30' : 'bg-white/5'
                          }`}
                        >
                          <button
                            onClick={() => toggleGoalComplete(goal.id, 'short')}
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                              goal.completed 
                                ? 'bg-green-500 border-green-500' 
                                : 'border-white/30 hover:border-aura-primary'
                            }`}
                          >
                            {goal.completed && <Check className="w-4 h-4 text-white" />}
                          </button>
                          <div className={`w-8 h-8 rounded-lg ${getCategoryColor(goal.category)} flex items-center justify-center`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1">
                            <p className={`font-medium ${goal.completed ? 'text-green-400 line-through' : 'text-white'}`}>
                              {goal.title}
                            </p>
                            {goal.description && (
                              <p className="text-sm text-aura-text-muted">{goal.description}</p>
                            )}
                          </div>
                          {goal.deadline && (
                            <Badge variant="outline" className="text-xs">
                              {format(new Date(goal.deadline), 'MMM d')}
                            </Badge>
                          )}
                          <button
                            onClick={() => deleteGoal(goal.id, 'short')}
                            className="p-1 text-aura-text-muted hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Clock className="w-10 h-10 text-aura-text-subtle mx-auto mb-2" />
                    <p className="text-aura-text-muted">No short-term goals yet</p>
                    <p className="text-xs text-aura-text-subtle mt-1">What do you want to accomplish this week?</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Long Term Goals */}
            <Card className="bg-aura-surface border-white/10">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-aura-primary" />
                      Long-Term Goals
                    </CardTitle>
                    <p className="text-sm text-aura-text-muted mt-1">
                      Season/year vision ({completedLongTerm}/{profile.longTermGoals.length} complete)
                    </p>
                  </div>
                  <Button 
                    size="sm"
                    onClick={() => { setGoalType('long'); setShowGoalDialog(true); }}
                    className="bg-aura-primary hover:bg-aura-primary-hover text-white"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Goal
                  </Button>
                </div>
                {profile.longTermGoals.length > 0 && (
                  <Progress 
                    value={(completedLongTerm / profile.longTermGoals.length) * 100} 
                    className="h-2 mt-3" 
                  />
                )}
              </CardHeader>
              <CardContent>
                {profile.longTermGoals.length > 0 ? (
                  <div className="space-y-3">
                    {profile.longTermGoals.map((goal) => {
                      const Icon = getCategoryIcon(goal.category);
                      return (
                        <div 
                          key={goal.id}
                          className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                            goal.completed ? 'bg-green-500/10 border border-green-500/30' : 'bg-white/5'
                          }`}
                        >
                          <button
                            onClick={() => toggleGoalComplete(goal.id, 'long')}
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                              goal.completed 
                                ? 'bg-green-500 border-green-500' 
                                : 'border-white/30 hover:border-aura-primary'
                            }`}
                          >
                            {goal.completed && <Check className="w-4 h-4 text-white" />}
                          </button>
                          <div className={`w-8 h-8 rounded-lg ${getCategoryColor(goal.category)} flex items-center justify-center`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1">
                            <p className={`font-medium ${goal.completed ? 'text-green-400 line-through' : 'text-white'}`}>
                              {goal.title}
                            </p>
                            {goal.description && (
                              <p className="text-sm text-aura-text-muted">{goal.description}</p>
                            )}
                          </div>
                          {goal.deadline && (
                            <Badge variant="outline" className="text-xs">
                              {format(new Date(goal.deadline), 'MMM yyyy')}
                            </Badge>
                          )}
                          <button
                            onClick={() => deleteGoal(goal.id, 'long')}
                            className="p-1 text-aura-text-muted hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Trophy className="w-10 h-10 text-aura-text-subtle mx-auto mb-2" />
                    <p className="text-aura-text-muted">No long-term goals yet</p>
                    <p className="text-xs text-aura-text-subtle mt-1">Where do you see yourself at the end of this season?</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Career Goal */}
            <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/30">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-bold text-lg text-white uppercase tracking-wider mb-2">
                      Ultimate Career Goal
                    </h3>
                    {profile.careerGoal ? (
                      <div>
                        <p className="text-white text-lg">{profile.careerGoal}</p>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => {
                            const newCareerGoal = prompt('What is your ultimate career goal?', profile.careerGoal);
                            if (newCareerGoal) saveProfile({ careerGoal: newCareerGoal });
                          }}
                          className="text-purple-400 mt-2"
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                      </div>
                    ) : (
                      <div>
                        <p className="text-aura-text-muted mb-3">
                          What's your ultimate dream? Play D1? Go Pro? Make the Olympic team?
                        </p>
                        <Button 
                          onClick={() => {
                            const careerGoal = prompt('What is your ultimate career goal?');
                            if (careerGoal) saveProfile({ careerGoal });
                          }}
                          className="bg-purple-500 hover:bg-purple-600 text-white"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Set Career Goal
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Metrics Tab */}
          <TabsContent value="metrics" className="mt-6 space-y-6">
            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-aura-secondary" />
                  Combine Metrics
                </CardTitle>
                <p className="text-sm text-aura-text-muted mt-1">
                  Track your athletic measurements - update after testing
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {sportMetrics.map((metric) => {
                    const Icon = metric.icon;
                    const value = profile.metrics[metric.id];
                    const hasValue = value !== undefined && value !== '';
                    
                    let status = 'none';
                    if (hasValue) {
                      const numValue = parseFloat(value);
                      if (metric.unit === 'sec') {
                        status = numValue <= metric.eliteValue ? 'elite' : numValue <= metric.goodValue ? 'good' : 'normal';
                      } else {
                        status = numValue >= metric.eliteValue ? 'elite' : numValue >= metric.goodValue ? 'good' : 'normal';
                      }
                    }

                    return (
                      <div 
                        key={metric.id}
                        className={`p-4 rounded-xl border transition-colors ${
                          status === 'elite' ? 'bg-aura-primary/10 border-aura-primary/30' :
                          status === 'good' ? 'bg-green-500/10 border-green-500/30' :
                          'bg-white/5 border-white/10'
                        }`}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            status === 'elite' ? 'bg-aura-primary' :
                            status === 'good' ? 'bg-green-500' :
                            'bg-white/10'
                          }`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-medium text-white">{metric.name}</p>
                            {status === 'elite' && <Badge className="bg-aura-primary/20 text-aura-primary border-0 text-xs">Elite</Badge>}
                            {status === 'good' && <Badge className="bg-green-500/20 text-green-400 border-0 text-xs">Good</Badge>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={value || ''}
                            onChange={(e) => updateMetric(metric.id, e.target.value)}
                            placeholder="--"
                            className="bg-aura-bg border-white/10 text-white text-center text-xl font-bold h-12"
                          />
                          <span className="text-aura-text-muted text-sm w-12">{metric.unit}</span>
                        </div>
                        <p className="text-xs text-aura-text-subtle mt-2">
                          Good: {metric.goodValue}{metric.unit} | Elite: {metric.eliteValue}{metric.unit}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile" className="mt-6 space-y-6">
            <div className="grid sm:grid-cols-2 gap-6">
              {/* Physical Stats */}
              <Card className="bg-aura-surface border-white/10">
                <CardHeader>
                  <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
                    Physical Stats
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-aura-text-muted text-xs uppercase">Height</Label>
                      <Input
                        value={profile.height}
                        onChange={(e) => setProfile({...profile, height: e.target.value})}
                        onBlur={() => saveProfile({ height: profile.height })}
                        placeholder="5'10&quot;"
                        className="bg-aura-bg border-white/10 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-aura-text-muted text-xs uppercase">Weight</Label>
                      <Input
                        value={profile.weight}
                        onChange={(e) => setProfile({...profile, weight: e.target.value})}
                        onBlur={() => saveProfile({ weight: profile.weight })}
                        placeholder="175 lbs"
                        className="bg-aura-bg border-white/10 text-white mt-1"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Academic Stats */}
              <Card className="bg-aura-surface border-white/10">
                <CardHeader>
                  <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
                    Academic Stats
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-aura-text-muted text-xs uppercase">GPA</Label>
                    <Input
                      value={profile.gpa}
                      onChange={(e) => setProfile({...profile, gpa: e.target.value})}
                      onBlur={() => saveProfile({ gpa: profile.gpa })}
                      placeholder="3.5"
                      className="bg-aura-bg border-white/10 text-white mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-aura-text-muted text-xs uppercase">SAT Score</Label>
                      <Input
                        value={profile.satScore}
                        onChange={(e) => setProfile({...profile, satScore: e.target.value})}
                        onBlur={() => saveProfile({ satScore: profile.satScore })}
                        placeholder="1200"
                        className="bg-aura-bg border-white/10 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-aura-text-muted text-xs uppercase">ACT Score</Label>
                      <Input
                        value={profile.actScore}
                        onChange={(e) => setProfile({...profile, actScore: e.target.value})}
                        onBlur={() => saveProfile({ actScore: profile.actScore })}
                        placeholder="28"
                        className="bg-aura-bg border-white/10 text-white mt-1"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Social Links */}
              <Card className="bg-aura-surface border-white/10 sm:col-span-2">
                <CardHeader>
                  <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
                    Social & Highlight Links
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-aura-text-muted text-xs uppercase flex items-center gap-2">
                        <Youtube className="w-4 h-4 text-red-500" />
                        Highlight Reel URL
                      </Label>
                      <Input
                        value={profile.highlightReelUrl}
                        onChange={(e) => setProfile({...profile, highlightReelUrl: e.target.value})}
                        onBlur={() => saveProfile({ highlightReelUrl: profile.highlightReelUrl })}
                        placeholder="https://youtube.com/..."
                        className="bg-aura-bg border-white/10 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-aura-text-muted text-xs uppercase flex items-center gap-2">
                        <LinkIcon className="w-4 h-4 text-blue-400" />
                        Hudl Profile
                      </Label>
                      <Input
                        value={profile.hudlUrl}
                        onChange={(e) => setProfile({...profile, hudlUrl: e.target.value})}
                        onBlur={() => saveProfile({ hudlUrl: profile.hudlUrl })}
                        placeholder="https://hudl.com/..."
                        className="bg-aura-bg border-white/10 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-aura-text-muted text-xs uppercase flex items-center gap-2">
                        <Instagram className="w-4 h-4 text-pink-500" />
                        Instagram
                      </Label>
                      <Input
                        value={profile.instagramHandle}
                        onChange={(e) => setProfile({...profile, instagramHandle: e.target.value})}
                        onBlur={() => saveProfile({ instagramHandle: profile.instagramHandle })}
                        placeholder="@username"
                        className="bg-aura-bg border-white/10 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-aura-text-muted text-xs uppercase flex items-center gap-2">
                        <Twitter className="w-4 h-4 text-blue-400" />
                        Twitter/X
                      </Label>
                      <Input
                        value={profile.twitterHandle}
                        onChange={(e) => setProfile({...profile, twitterHandle: e.target.value})}
                        onBlur={() => saveProfile({ twitterHandle: profile.twitterHandle })}
                        placeholder="@username"
                        className="bg-aura-bg border-white/10 text-white mt-1"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Goal Dialog */}
        <Dialog open={showGoalDialog} onOpenChange={setShowGoalDialog}>
          <DialogContent className="bg-aura-surface border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-xl text-white uppercase tracking-wider">
                Add {goalType === 'short' ? 'Short-Term' : 'Long-Term'} Goal
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-aura-text-muted text-xs uppercase">Goal Title</Label>
                <Input
                  value={newGoal.title}
                  onChange={(e) => setNewGoal({...newGoal, title: e.target.value})}
                  placeholder="e.g., Run a 4.5 40-yard dash"
                  className="bg-aura-bg border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-aura-text-muted text-xs uppercase">Description (optional)</Label>
                <Textarea
                  value={newGoal.description}
                  onChange={(e) => setNewGoal({...newGoal, description: e.target.value})}
                  placeholder="Why is this important?"
                  className="bg-aura-bg border-white/10 text-white mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-aura-text-muted text-xs uppercase">Category</Label>
                  <Select value={newGoal.category} onValueChange={(v) => setNewGoal({...newGoal, category: v})}>
                    <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-aura-surface border-white/10">
                      <SelectItem value="training" className="text-white">Training</SelectItem>
                      <SelectItem value="academics" className="text-white">Academics</SelectItem>
                      <SelectItem value="nutrition" className="text-white">Nutrition</SelectItem>
                      <SelectItem value="mental" className="text-white">Mental</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-aura-text-muted text-xs uppercase">Deadline</Label>
                  <Input
                    type="date"
                    value={newGoal.deadline}
                    onChange={(e) => setNewGoal({...newGoal, deadline: e.target.value})}
                    className="bg-aura-bg border-white/10 text-white mt-1"
                  />
                </div>
              </div>
              <Button 
                onClick={addGoal}
                disabled={!newGoal.title}
                className="w-full bg-aura-primary hover:bg-aura-primary-hover text-white"
              >
                Add Goal
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* School Selection Dialog */}
        <Dialog open={showSchoolDialog} onOpenChange={setShowSchoolDialog}>
          <DialogContent className="bg-aura-surface border-white/10 max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-xl text-white uppercase tracking-wider">
                Select Dream School
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-2 mt-4">
              {POPULAR_SCHOOLS.filter(s => !profile.dreamSchools.includes(s.id)).map((school) => (
                <button
                  key={school.id}
                  onClick={() => addDreamSchool(school.id)}
                  className="w-full flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors text-left"
                >
                  <div className="w-10 h-10 bg-aura-secondary/20 rounded-full flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-aura-secondary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white">{school.name}</p>
                    {school.conference && (
                      <p className="text-xs text-aura-text-muted">{school.conference}</p>
                    )}
                  </div>
                  <Plus className="w-5 h-5 text-aura-text-muted" />
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Why Statement Dialog */}
        <Dialog open={showWhyDialog} onOpenChange={setShowWhyDialog}>
          <DialogContent className="bg-aura-surface border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-xl text-white uppercase tracking-wider">
                Your "Why" Statement
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-aura-text-muted text-sm">
                Write why you train, what drives you, and what you're committed to achieving. 
                This will remind you on hard days why you started.
              </p>
              <Textarea
                value={profile.whyStatement}
                onChange={(e) => setProfile({...profile, whyStatement: e.target.value})}
                placeholder="I train because... I'm committed to... My dream is..."
                className="bg-aura-bg border-white/10 text-white min-h-[150px]"
              />
              <Button 
                onClick={() => {
                  saveProfile({ whyStatement: profile.whyStatement });
                  setShowWhyDialog(false);
                }}
                className="w-full bg-aura-primary hover:bg-aura-primary-hover text-white"
              >
                Save My "Why"
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AthleteProfilePage;
